import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import TermsApi from "../../api/subscription/TermsApi";
import { getApiErrorMessage } from "../../utils/getApiErrorMessage";
import {
  NotificationVariant,
  useNotifications,
} from "../../components/dashboard/NotificationsProvider";
import { useSelector } from "../../store";
import { generateHumanDisplayName } from "../../utils/DataTransformationUtils";
import { TermsVersion } from "../../types/terms";
import TermsContentScroll from "./TermsContentScroll";

interface TermsAcceptanceModalProps {
  open: boolean;
  onClose: () => void;
  orgId: string;
  terms?: TermsVersion | null;
  onAccepted: () => void;
}

const TERMS_LOAD_ERROR_MESSAGE =
  "We couldn't load Terms & Conditions. Check your connection and try again.";

const TERMS_UNAVAILABLE_MESSAGE =
  "Terms & Conditions are not available yet. Ask an administrator to publish them, then try again.";

const SIGNATURE_PAD_HEIGHT = 120;

function syncSignatureCanvasToDisplay(
  pad: {
    clear: () => void;
    isEmpty: () => boolean;
    getCanvas?: () => HTMLCanvasElement;
  } | null,
) {
  const canvas = pad?.getCanvas?.();
  if (!canvas || pad.isEmpty() === false) {
    return;
  }
  const ratio = Math.max(window.devicePixelRatio || 1, 1);
  const width = Math.round(canvas.offsetWidth * ratio);
  const height = Math.round(canvas.offsetHeight * ratio);
  if (width <= 0 || height <= 0) {
    return;
  }
  if (canvas.width === width && canvas.height === height) {
    return;
  }
  canvas.width = width;
  canvas.height = height;
  canvas.getContext("2d")?.scale(ratio, ratio);
  pad.clear();
}

const TermsAcceptanceModal = ({
  open,
  onClose,
  orgId,
  terms,
  onAccepted,
}: TermsAcceptanceModalProps) => {
  const signatureRef = useRef<SignatureCanvas | null>(null);
  const signatureBoxRef = useRef<HTMLDivElement | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadedTerms, setLoadedTerms] = useState<TermsVersion | null>(
    terms || null,
  );
  const [termsUnavailable, setTermsUnavailable] = useState(false);
  const [termsLoadError, setTermsLoadError] = useState(false);
  const { setNotification } = useNotifications();
  const profile = useSelector((state) => state.user.profile);
  const practitionerReference = useSelector(
    (state) => state.user.practitionerReference,
  );
  const signerName =
    generateHumanDisplayName(profile?.name) ||
    practitionerReference?.display ||
    "";
  const termsApi = useMemo(() => new TermsApi(), []);

  useEffect(() => {
    if (!open) {
      setTermsUnavailable(false);
      setTermsLoadError(false);
      return undefined;
    }
    if (terms) {
      setLoadedTerms(terms);
      setTermsUnavailable(false);
      setTermsLoadError(false);
      return undefined;
    }
    let cancelled = false;
    setTermsUnavailable(false);
    setTermsLoadError(false);
    termsApi
      .getCurrent("subscription")
      .then((current) => {
        if (!cancelled) {
          setLoadedTerms(current);
          setTermsUnavailable(!current);
          setTermsLoadError(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoadedTerms(null);
          setTermsUnavailable(false);
          setTermsLoadError(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, terms, termsApi]);

  const activeTerms = terms || loadedTerms;

  useEffect(() => {
    if (!open || !activeTerms || termsUnavailable) {
      return undefined;
    }
    const box = signatureBoxRef.current;
    const sync = () => syncSignatureCanvasToDisplay(signatureRef.current);
    sync();
    const frame = window.requestAnimationFrame(sync);
    if (!box || typeof ResizeObserver === "undefined") {
      return () => window.cancelAnimationFrame(frame);
    }
    const observer = new ResizeObserver(sync);
    observer.observe(box);
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [open, activeTerms, termsUnavailable]);

  const signaturePresent = () => !signatureRef.current?.isEmpty();

  const canSubmit =
    !!activeTerms?.id && agreed && hasSignature && !submitting && !!orgId;

  const updateSignatureState = useCallback(() => {
    setHasSignature(
      Boolean(signatureRef.current && !signatureRef.current.isEmpty()),
    );
  }, []);

  const handleClear = () => {
    signatureRef.current?.clear();
    setHasSignature(false);
  };

  const handleDecline = () => {
    if (submitting) return;
    handleClear();
    setAgreed(false);
    onClose();
  };

  const preventDialogClose = () => {
    // The modal is intentionally closed only by explicit Cancel/Accept actions.
  };

  const handleAccept = async () => {
    if (!canSubmit || !activeTerms?.id || !signatureRef.current) {
      return;
    }
    if (signatureRef.current.isEmpty()) {
      setHasSignature(false);
      return;
    }
    const signature = signatureRef.current.toDataURL("image/png");
    setSubmitting(true);
    try {
      await termsApi.accept(activeTerms.id, {
        organizationId: orgId,
        signature,
        accepted: true,
      });
      handleClear();
      setAgreed(false);
      onAccepted();
    } catch (error) {
      setNotification({
        message: getApiErrorMessage(
          error,
          "Unable to accept Terms & Conditions.",
        ),
        variant: NotificationVariant.error,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={preventDialogClose}
      disableEscapeKeyDown
      fullWidth
      maxWidth="md"
      data-testid="terms-acceptance-modal"
      sx={{
        "& .MuiDialog-paper": {
          height: "90%",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle>
        Terms & Conditions
        {activeTerms?.version ? ` · Version ${activeTerms.version}` : ""}
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {termsUnavailable || termsLoadError ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 2,
            }}
          >
            <Typography
              variant="body1"
              color="text.secondary"
              align="center"
              data-testid={
                termsLoadError ? "terms-load-error" : "terms-unavailable"
              }
            >
              {termsLoadError
                ? TERMS_LOAD_ERROR_MESSAGE
                : TERMS_UNAVAILABLE_MESSAGE}
            </Typography>
          </Box>
        ) : !activeTerms ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={24} />
          </Box>
        ) : (
          <TermsContentScroll
            testId="terms-content"
            html={activeTerms.sanitizedContent || ""}
          />
        )}

        {activeTerms && !termsUnavailable ? (
          <Box sx={{ flexShrink: 0, mt: "auto" }}>
            <Divider sx={{ my: 2 }} data-testid="terms-signature-divider" />
            <Typography variant="subtitle2" gutterBottom>
              Electronic Signature
            </Typography>
            {signerName ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Signing as {signerName}
              </Typography>
            ) : null}
            <Box
              ref={signatureBoxRef}
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                mb: 1,
                touchAction: "none",
              }}
            >
              <SignatureCanvas
                ref={signatureRef}
                penColor="black"
                clearOnResize={false}
                onEnd={updateSignatureState}
                canvasProps={{
                  style: {
                    width: "100%",
                    height: SIGNATURE_PAD_HEIGHT,
                    display: "block",
                  },
                  "data-testid": "terms-signature-pad",
                }}
              />
            </Box>
            <Button size="small" onClick={handleClear}>
              Clear Signature
            </Button>
            <Box mt={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreed}
                    onChange={(event) => setAgreed(event.target.checked)}
                    inputProps={{ "aria-label": "agree-to-terms" }}
                  />
                }
                label="I have read and agree to the Terms & Conditions."
              />
            </Box>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleDecline}
          disabled={submitting}
          data-testid="terms-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAccept}
          disabled={!canSubmit}
          data-testid="accept-and-sign"
        >
          {submitting ? "Signing…" : "Accept & Sign"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TermsAcceptanceModal;
